/**
 * Real-time Transcription Service
 *
 * Generic WebSocket-based audio streaming client. Streams 16kHz / 16-bit PCM
 * to a configurable backend endpoint that handles transcription
 * (e.g. Azure Speech Services with auto EN/FR detection).
 *
 * Backend contract:
 *   - Client sends binary frames (raw 16-bit PCM, 16kHz, mono).
 *   - Client may send JSON control frames: { type: "stop" } to flush + close.
 *   - Backend sends JSON frames:
 *       { type: "transcript_partial", text, language?, confidence? }
 *       { type: "transcript_final",   text, language?, confidence? }
 *       { type: "error" | "warning",  message }
 */

export type TranscriptionEventType =
  | 'connected'
  | 'disconnected'
  | 'transcript_partial'
  | 'transcript_final'
  | 'error'
  | 'warning';

export interface TranscriptionEvent {
  type: TranscriptionEventType;
  data?: {
    text?: string;
    language?: string;
    confidence?: number;
    message?: string;
  };
}

export type TranscriptionEventCallback = (event: TranscriptionEvent) => void;

export class RealtimeTranscriptionService {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private isConnected: boolean = false;
  private eventCallbacks: TranscriptionEventCallback[] = [];

  /**
   * Connect to a transcription WebSocket endpoint.
   *
   * @param wsUrl - Full WebSocket URL (ws:// or wss://). Caller builds it
   *                (including any auth query params).
   */
  async connect(wsUrl: string): Promise<void> {
    if (this.isConnected) {
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.isConnected = true;
          this.emitEvent({ type: 'connected' });
          resolve();
        };

        this.ws.onerror = (error) => {
          this.emitEvent({ type: 'error', data: { message: 'Connection error' } });
          reject(error);
        };

        this.ws.onclose = () => {
          this.isConnected = false;
          this.emitEvent({ type: 'disconnected' });
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            switch (data.type) {
              case 'transcript_partial':
              case 'transcript_final':
                this.emitEvent({
                  type: data.type,
                  data: {
                    text: data.text,
                    language: data.language,
                    confidence: data.confidence,
                  },
                });
                break;
              case 'error':
              case 'warning':
                this.emitEvent({ type: data.type, data: { message: data.message } });
                break;
            }
          } catch {
            // Ignore malformed frames.
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Start streaming audio from a MediaStream to the transcription service.
   */
  startStreaming(audioStream: MediaStream): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const audioTracks = audioStream.getAudioTracks();
    if (audioTracks.length === 0) {
      this.emitEvent({ type: 'error', data: { message: 'No audio tracks in stream' } });
      return;
    }

    try {
      // Azure Speech expects 16kHz mono PCM.
      this.audioContext = new AudioContext({ sampleRate: 16000 });
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(audioStream);
      this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

      this.scriptProcessor.onaudioprocess = (event) => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          return;
        }
        const inputData = event.inputBuffer.getChannelData(0);
        this.ws.send(this.floatTo16BitPCM(inputData));
      };

      this.mediaStreamSource.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
    } catch (error) {
      this.emitEvent({
        type: 'error',
        data: { message: error instanceof Error ? error.message : 'Failed to start audio streaming' },
      });
    }
  }

  /**
   * Stop the audio pipeline but keep the WebSocket open so the server can
   * flush a final transcript before the caller disconnects.
   */
  stopStreaming(): void {
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }
  }

  /** Ask the server to finalize the current transcript and close the session. */
  finalize(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop' }));
    }
  }

  disconnect(): void {
    this.stopStreaming();
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }
    this.isConnected = false;
  }

  onEvent(callback: TranscriptionEventCallback): () => void {
    this.eventCallbacks.push(callback);
    return () => {
      const index = this.eventCallbacks.indexOf(callback);
      if (index > -1) {
        this.eventCallbacks.splice(index, 1);
      }
    };
  }

  get connected(): boolean {
    return this.isConnected;
  }

  /** Convert Float32Array audio samples to 16-bit PCM (little-endian). */
  private floatTo16BitPCM(float32Array: Float32Array): ArrayBuffer {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  }

  private emitEvent(event: TranscriptionEvent): void {
    for (const callback of this.eventCallbacks) {
      try {
        callback(event);
      } catch {
        // Swallow callback errors so one bad handler doesn't break others.
      }
    }
  }
}
